import { alpha } from "@mui/material/styles";
import { getAuthGlassFieldSx } from "./Auth/authGlassStyles";
import { getMFPrimaryButtonSx, getMFSecondaryButtonSx } from "../utils/dialogStyles";
import { shapeShell } from "../theme/shape";
import { externalFlowSuccessIconWellSx } from "./UploadStates/uploadCardLayout";
import { legalLinkSx } from "../utils/legalLinks";

/** Above gallery/editor chrome (version chips, overflow sheets, footer gradients). */
export const GALLERY_INQUIRY_MODAL_Z_INDEX = 14000;

/** Top-anchor on mobile so the soft keyboard doesn't shove a centred modal off-screen. */
export function getGalleryInquiryDialogSx() {
  return {
    zIndex: GALLERY_INQUIRY_MODAL_Z_INDEX,
    "& .MuiDialog-container": {
      alignItems: { xs: "flex-start", sm: "center" },
    },
  };
}

export function getGalleryInquiryPaperSx() {
  return {
    borderRadius: shapeShell,
    width: { xs: "min(92vw, 400px)", sm: "400px" },
    maxWidth: { xs: "min(92vw, 400px)", sm: "400px" },
    marginLeft: { xs: "16px", sm: "24px" },
    marginRight: { xs: "16px", sm: "24px" },
    marginBottom: { xs: "16px", sm: "24px" },
    marginTop: {
      xs: "max(env(safe-area-inset-top, 0px) + 12px, 20px)",
      sm: "24px",
    },
    p: { xs: 2.5, sm: 3 },
    boxSizing: "border-box",
    // The shared paper hides overflow under a capped maxHeight, so a short
    // viewport (landscape phone, small desktop window) would clip the CTA with
    // no way to reach it. Scroll on Y only; X stays hidden.
    overflowY: "auto",
  };
}

export const galleryInquiryTitleSx = {
  fontSize: { xs: "1.0625rem", sm: "1.125rem" },
  fontWeight: 600,
  letterSpacing: "-0.02em",
  lineHeight: 1.35,
  color: "text.primary",
};

export const galleryInquiryBodySx = {
  fontSize: "15px",
  lineHeight: 1.5,
  color: "text.secondary",
};

export const galleryInquiryCaptionSx = {
  fontSize: "11px",
  fontWeight: 400,
  lineHeight: 1.4,
  color: "rgba(0,0,0,0.45)",
};

/**
 * Inline Terms / Privacy inside consent copy. Same canonical destinations as
 * `AuthLegalStrip`, but always underlined so each link reads as tappable on
 * touch (where there is no hover to reveal the shared token's underline).
 */
export const galleryInquiryLegalLinkSx = {
  ...legalLinkSx,
  fontSize: "inherit",
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": { textDecoration: "underline" },
};

/** Top-right dismiss control — same recipe as `StandardDialog`'s close button. */
export function getGalleryInquiryCloseButtonSx(theme) {
  return {
    position: "absolute",
    right: 12,
    top: 12,
    color: "text.secondary",
    "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.08) },
  };
}

/** Leaves room for the close control so headings never run under it. */
export const galleryInquiryHeaderInsetSx = { pr: 4 };

/** Success hero — the upload-complete card's circular well, centred in the modal. */
export const galleryInquirySuccessIconWellSx = {
  ...externalFlowSuccessIconWellSx,
  mx: "auto",
  mb: 1.5,
};

/**
 * Unified primary CTA — brand pill with auto-contrast label. Same primitive
 * as AuthModal / login / signup / editor Apply + Make Magic, so the app has
 * one primary CTA recipe across every surface.
 */
export function getGalleryInquiryPrimaryCtaSx(theme) {
  return {
    ...getMFPrimaryButtonSx(theme),
    "&.Mui-disabled": { color: "#ffffff", opacity: 0.55 },
  };
}

export function getGalleryInquirySecondaryCtaSx(theme) {
  return {
    ...getMFSecondaryButtonSx(),
    borderColor: alpha(theme.palette.text.primary, 0.12),
    color: "text.primary",
  };
}

export function getGalleryInquiryPrimaryFullWidthCtaSx(theme) {
  return {
    ...getGalleryInquiryPrimaryCtaSx(theme),
    width: "100%",
  };
}

/** Above gallery/editor chrome, below the inquiry modal, which can open over it. */
export const GALLERY_INTEREST_TOAST_Z_INDEX = 13000;

/**
 * Clears the gallery's fixed pill row, which sits 26px above the safe area.
 * Narrow screens stack "Shuffle it" above the Share / I'm Interested pair, so
 * xs leaves room for two pill rows rather than one.
 */
export const galleryInterestToastBottom = {
  xs: "calc(max(16px, env(safe-area-inset-bottom, 16px)) + 138px)",
  sm: "calc(max(16px, env(safe-area-inset-bottom, 16px)) + 86px)",
};

export function getGalleryInterestToastWrapperSx(bottom) {
  return {
    position: "fixed",
    bottom,
    left: 0,
    right: 0,
    zIndex: GALLERY_INTEREST_TOAST_Z_INDEX,
    display: "flex",
    justifyContent: "center",
    px: 2,
    // The strip spans the viewport so the card can centre; only the card itself
    // should intercept taps meant for the plan behind it.
    pointerEvents: "none",
    "@keyframes galleryInterestToastIn": {
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  };
}

/** Same glass card recipe as the editor's floating toasts. */
export const galleryInterestToastCardSx = {
  pointerEvents: "auto",
  position: "relative",
  width: "100%",
  maxWidth: 360,
  boxSizing: "border-box",
  borderRadius: "14px",
  background: "rgba(248, 248, 250, 0.96)",
  border: "1px solid rgba(0, 0, 0, 0.07)",
  boxShadow:
    "0 8px 28px rgba(0, 0, 0, 0.14), 0 1px 0 rgba(255, 255, 255, 0.9) inset",
  pl: 1.75,
  pr: 4.5,
  py: 1.25,
  animation: "galleryInterestToastIn 200ms ease-out both",
};

export const galleryInterestToastTitleSx = {
  fontSize: "14px",
  fontWeight: 600,
  letterSpacing: "-0.01em",
  lineHeight: 1.35,
  color: "text.primary",
};

export const galleryInterestToastBodySx = {
  fontSize: "13px",
  lineHeight: 1.45,
  color: "text.secondary",
};

export function getGalleryInterestToastCloseButtonSx(theme) {
  return {
    position: "absolute",
    right: 4,
    top: 4,
    color: "text.secondary",
    "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.08) },
  };
}

/** Reuses auth glass fields; slightly stronger idle tint on white modal paper. */
export function getGalleryInquiryEmailFieldSx(theme) {
  const base = getAuthGlassFieldSx(theme);
  return {
    ...base,
    "& .MuiOutlinedInput-root": {
      ...base["& .MuiOutlinedInput-root"],
      backgroundColor: "rgba(0,0,0,0.08)",
    },
  };
}
